/**
 * Every closed value set that crosses the wire, as a `const` tuple plus its zod enum.
 *
 * These tuples are the single place the API surface spells them out. `@crm/domain`
 * exports the same tuples for the pure-function layer (`CONTACT_TYPES`, `CHANNELS`,
 * `INTERACTION_KINDS`, `SHARE_SCOPES`); `@crm/contracts` deliberately does NOT depend
 * on `@crm/domain` (it must stay importable by a service worker with zero transitive
 * weight), so the overlapping tuples are duplicated here and must be kept identical.
 * They are also the mirror of the Postgres enums in `@crm/db`.
 */

import { z } from 'zod';

// --- tenancy -----------------------------------------------------------------

/** better-auth organization roles. */
export const ORG_ROLES = ['owner', 'admin', 'member'] as const;
export type OrgRole = (typeof ORG_ROLES)[number];
export const orgRoleSchema = z.enum(ORG_ROLES);

/**
 * `org_settings.recording_default_consent_mode`. The design fixes the default
 * (`disclose_always`) but not the full set; these are the three postures the macOS
 * recorder implements. Stored as `text` in Postgres, so widening is cheap.
 */
export const RECORDING_CONSENT_MODES = [
  'disclose_always',
  'disclose_two_party_only',
  'never_record',
] as const;
export type RecordingConsentMode = (typeof RECORDING_CONSENT_MODES)[number];
export const recordingConsentModeSchema = z.enum(RECORDING_CONSENT_MODES);

// --- contacts ----------------------------------------------------------------

/** Mirror of `@crm/domain`'s `CONTACT_TYPES` and the `contact_type` pg enum. */
export const CONTACT_TYPES = ['investor', 'founder', 'ecosystem', 'employee'] as const;
export type ContactType = (typeof CONTACT_TYPES)[number];
export const contactTypeSchema = z.enum(CONTACT_TYPES);

/** `contact_visibility` pg enum — applies to contacts, lists and interactions. */
export const VISIBILITIES = ['org', 'private'] as const;
export type Visibility = (typeof VISIBILITIES)[number];
export const visibilitySchema = z.enum(VISIBILITIES);

export const EMAIL_LABELS = ['work', 'personal', 'other'] as const;
export type EmailLabel = (typeof EMAIL_LABELS)[number];
export const emailLabelSchema = z.enum(EMAIL_LABELS);

export const PHONE_LABELS = ['mobile', 'work', 'home', 'other'] as const;
export type PhoneLabel = (typeof PHONE_LABELS)[number];
export const phoneLabelSchema = z.enum(PHONE_LABELS);

/** `email_verification_status` pg enum. Hermes will not auto-send to non-deliverable. */
export const EMAIL_VERIFICATION_STATUSES = [
  'deliverable',
  'high_probability',
  'catch_all',
  'invalid',
  'unknown',
] as const;
export type EmailVerificationStatus = (typeof EMAIL_VERIFICATION_STATUSES)[number];
export const emailVerificationStatusSchema = z.enum(EMAIL_VERIFICATION_STATUSES);

/** Provenance of a field value. Rendered by the web `ProvenanceField` primitive. */
export const FIELD_SOURCES = [
  'user',
  'extension',
  'signature',
  'phone_book',
  'address_book',
  'archive',
  'gmail',
  'graph',
  'fullenrich',
  'pdl',
  'hunter',
  'apollo_byok',
] as const;
export type FieldSource = (typeof FIELD_SOURCES)[number];
export const fieldSourceSchema = z.enum(FIELD_SOURCES);

export const ENRICHMENT_STATUSES = [
  'none',
  'queued',
  'running',
  'enriched',
  'failed',
  'suppressed',
] as const;
export type EnrichmentStatus = (typeof ENRICHMENT_STATUSES)[number];
export const enrichmentStatusSchema = z.enum(ENRICHMENT_STATUSES);

/** Sort keys offered by `contacts.list`; each has a matching cursor encoding. */
export const CONTACT_SORTS = ['updated_at', 'last_interaction_at', 'name'] as const;
export type ContactSort = (typeof CONTACT_SORTS)[number];
export const contactSortSchema = z.enum(CONTACT_SORTS);

// --- sharing -----------------------------------------------------------------

/**
 * Mirror of `@crm/domain`'s `SHARE_SCOPES` and the `share_scope` pg enum.
 * - `identity`       name, type, company, title, linkedin, primary email/phone
 * - `identity_notes` identity + owner's notes + interaction titles (no bodies)
 * - `full`           identity_notes + interaction bodies
 *
 * Vendor-enriched fields never cross a grant unless the owning org has opted in via
 * `org_settings.allowEnrichedFieldSharing` (default false).
 */
export const SHARE_SCOPES = ['identity', 'identity_notes', 'full'] as const;
export type ShareScope = (typeof SHARE_SCOPES)[number];
export const shareScopeSchema = z.enum(SHARE_SCOPES);

/** `share_principal` pg enum — a grant targets an org or a single user. */
export const SHARE_PRINCIPALS = ['org', 'user'] as const;
export type SharePrincipal = (typeof SHARE_PRINCIPALS)[number];
export const sharePrincipalSchema = z.enum(SHARE_PRINCIPALS);

export const SHARE_RESOURCE_TYPES = ['contact', 'list'] as const;
export type ShareResourceType = (typeof SHARE_RESOURCE_TYPES)[number];
export const shareResourceTypeSchema = z.enum(SHARE_RESOURCE_TYPES);

/** How the caller reaches a record: it is theirs, or it was granted to them. */
export const ACCESS_SOURCES = ['owned', 'shared'] as const;
export type AccessSource = (typeof ACCESS_SOURCES)[number];
export const accessSourceSchema = z.enum(ACCESS_SOURCES);

/** The `source` filter on `contacts.list` adds an `all` fan-in. */
export const ACCESS_SOURCE_FILTERS = ['owned', 'shared', 'all'] as const;
export type AccessSourceFilter = (typeof ACCESS_SOURCE_FILTERS)[number];
export const accessSourceFilterSchema = z.enum(ACCESS_SOURCE_FILTERS);

// --- lists -------------------------------------------------------------------

export const LIST_KINDS = ['static', 'smart'] as const;
export type ListKind = (typeof LIST_KINDS)[number];
export const listKindSchema = z.enum(LIST_KINDS);

// --- events ------------------------------------------------------------------

export const EVENT_KINDS = ['conference', 'dinner', 'demo_day', 'meeting', 'other'] as const;
export type EventKind = (typeof EVENT_KINDS)[number];
export const eventKindSchema = z.enum(EVENT_KINDS);

export const EVENT_SOURCES = ['manual', 'calendar'] as const;
export type EventSource = (typeof EVENT_SOURCES)[number];
export const eventSourceSchema = z.enum(EVENT_SOURCES);

/** Why `events.current` proposed a candidate. Drives the add-contact chip copy. */
export const EVENT_CANDIDATE_REASONS = [
  'in_progress',
  'starting_soon',
  'just_ended',
  'recent',
] as const;
export type EventCandidateReason = (typeof EVENT_CANDIDATE_REASONS)[number];
export const eventCandidateReasonSchema = z.enum(EVENT_CANDIDATE_REASONS);

// --- interactions ------------------------------------------------------------

/** Mirror of `@crm/domain`'s `INTERACTION_KINDS` and the `interaction_kind` pg enum. */
export const INTERACTION_KINDS = [
  'met_at_event',
  'meeting',
  'coffee',
  'call',
  'email',
  'linkedin_message',
  'linkedin_connection',
  'imessage',
  'sms',
  'note',
  'task',
  'enrichment',
] as const;
export type InteractionKind = (typeof INTERACTION_KINDS)[number];
export const interactionKindSchema = z.enum(INTERACTION_KINDS);

/** Mirror of `@crm/domain`'s `CHANNELS` and the `channel` pg enum. */
export const CHANNELS = [
  'email',
  'linkedin',
  'imessage',
  'sms',
  'call',
  'in_person',
  'manual',
] as const;
export type Channel = (typeof CHANNELS)[number];
export const channelSchema = z.enum(CHANNELS);

export const DIRECTIONS = ['inbound', 'outbound', 'mutual', 'system'] as const;
export type Direction = (typeof DIRECTIONS)[number];
export const directionSchema = z.enum(DIRECTIONS);

/** `interactions.source_type` — which table `source_id` points into. */
export const INTERACTION_SOURCE_TYPES = [
  'meeting',
  'message_thread',
  'message',
  'calendar_event',
  'manual',
  'archive',
] as const;
export type InteractionSourceType = (typeof INTERACTION_SOURCE_TYPES)[number];
export const interactionSourceTypeSchema = z.enum(INTERACTION_SOURCE_TYPES);

// --- sync / calendar ---------------------------------------------------------

/** Mirror of the `provider` pg enum (`@crm/db` §1.13). */
export const SYNC_PROVIDERS = ['google', 'microsoft'] as const;
export type SyncProvider = (typeof SYNC_PROVIDERS)[number];
export const syncProviderSchema = z.enum(SYNC_PROVIDERS);

/**
 * `connected_accounts.status`. Mirrors the pg CHECK constraint exactly, so the
 * settings screen's health badge cannot render a state the database can hold.
 */
export const CONNECTED_ACCOUNT_STATUSES = [
  'active',
  'reauth_required',
  'revoked',
  'error',
  'paused',
] as const;
export type ConnectedAccountStatus = (typeof CONNECTED_ACCOUNT_STATUSES)[number];
export const connectedAccountStatusSchema = z.enum(CONNECTED_ACCOUNT_STATUSES);

/**
 * The DERIVED badge the clients design §2.9 asks for: "sync health
 * (`healthy | degraded | needs_reauth | revoked`)". Computed by the server from
 * status + watermark age + subscription expiry, never stored.
 */
export const SYNC_HEALTH_STATES = ['healthy', 'degraded', 'needs_reauth', 'revoked'] as const;
export type SyncHealth = (typeof SYNC_HEALTH_STATES)[number];
export const syncHealthSchema = z.enum(SYNC_HEALTH_STATES);

export const BACKFILL_STATES = ['pending', 'running', 'complete', 'failed'] as const;
export type BackfillState = (typeof BACKFILL_STATES)[number];
export const backfillStateSchema = z.enum(BACKFILL_STATES);

/** Mirror of `@crm/domain`'s `MEETING_PROVIDERS` and `conference_provider`. */
export const MEETING_PROVIDERS = ['zoom', 'meet', 'teams', 'webex', 'goto'] as const;
export type MeetingProvider = (typeof MEETING_PROVIDERS)[number];
export const meetingProviderSchema = z.enum(MEETING_PROVIDERS);

// --- transport ---------------------------------------------------------------

/** `X-Client` header values; drives per-client kill switches and min-version gates. */
export const CLIENT_KINDS = ['web', 'ios', 'macos', 'ext'] as const;
export type ClientKind = (typeof CLIENT_KINDS)[number];
export const clientKindSchema = z.enum(CLIENT_KINDS);

// --- intelligence + enrichment (Wave-1 track C) ------------------------------

/**
 * `enrichment_records.provider`, restricted to the three adapters this wave
 * ships. The pg enum is wider (it carries the free Stage-0 sources — extension,
 * signature, address_book — and the demoted `apollo_byok`); the WIRE surface
 * lists only what a caller can actually ask for.
 */
export const ENRICHMENT_PROVIDERS = ['apollo', 'pdl', 'fullenrich'] as const;
export type EnrichmentProviderId = (typeof ENRICHMENT_PROVIDERS)[number];
export const enrichmentProviderSchema = z.enum(ENRICHMENT_PROVIDERS);

/** `contact_field_candidates.status`. */
export const CANDIDATE_STATUSES = [
  'pending',
  'auto_applied',
  'accepted',
  'rejected',
  'superseded',
] as const;
export type CandidateStatus = (typeof CANDIDATE_STATUSES)[number];
export const candidateStatusSchema = z.enum(CANDIDATE_STATUSES);

/** What the merge layer decided, mirroring `mergeService`'s `MergeAction`. */
export const MERGE_ACTIONS = ['auto_apply', 'pending', 'reject'] as const;
export type MergeActionValue = (typeof MERGE_ACTIONS)[number];
export const mergeActionSchema = z.enum(MERGE_ACTIONS);

/** `enrichment_records.triggered_by` — a spend question is "who asked?". */
export const ENRICHMENT_TRIGGERS = [
  'user',
  'auto_on_create',
  'refresh',
  'bounce',
  'import',
] as const;
export type EnrichmentTrigger = (typeof ENRICHMENT_TRIGGERS)[number];
export const enrichmentTriggerSchema = z.enum(ENRICHMENT_TRIGGERS);

/** Mirror of the `commitment_kind` pg enum (§1.10). */
export const COMMITMENT_KINDS = [
  'intro_offered',
  'intro_requested',
  'promise',
  'ask',
  'action_item',
  'info_share',
] as const;
export type CommitmentKindValue = (typeof COMMITMENT_KINDS)[number];
export const commitmentKindSchema = z.enum(COMMITMENT_KINDS);

/**
 * `commitments.status`. `needs_review` is design §3.4's destination for a mined
 * commitment below the 0.6 confidence threshold: KEPT and excluded from
 * drafting, not dropped.
 */
export const COMMITMENT_STATUSES = [
  'open',
  'needs_review',
  'in_progress',
  'fulfilled',
  'dropped',
  'expired',
] as const;
export type CommitmentStatusValue = (typeof COMMITMENT_STATUSES)[number];
export const commitmentStatusSchema = z.enum(COMMITMENT_STATUSES);

/** `commitments.owed_by`: `me` = I owe it, `them` = they owe me. */
export const OWED_BY = ['me', 'them'] as const;
export type OwedBy = (typeof OWED_BY)[number];
export const owedBySchema = z.enum(OWED_BY);

/** The router's budget posture (design §1.8). */
export const BUDGET_STATES = ['ok', 'soft', 'hard'] as const;
export type BudgetStateValue = (typeof BUDGET_STATES)[number];
export const budgetStateSchema = z.enum(BUDGET_STATES);
